/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { FileText, Plus, Upload } from "lucide-react";
import { API } from "../../App";
// Sub-components extracted to di/ folder
import PilotCard from "./di/PilotCard";
import PilotForm from "./di/PilotForm";
import PilotMergeDialog from "./di/PilotMergeDialog";
import PilotsImportDialog from "./di/PilotsImportDialog";
import PendingPilotDocuments from "./di/PendingPilotDocuments";
import PendingFlightRequests from "./di/PendingFlightRequests";
import StudentsWithoutPilotCard from "./di/StudentsWithoutPilotCard";
import AlertsBanner from "./di/AlertsBanner";

// Re-export for backwards compatibility
export { default as PilotFlightForm } from "./di/PilotFlightForm";

export default function PilotsPanel({ onInstructorCreated }) {
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [mergeFor, setMergeFor] = useState(null);
  const [openIds, setOpenIds] = useState(() => new Set());     // cartões fechados por defeito: abre-se o que interessa
  // Documentos por piloto (o cartão só mostra o botão quando há algum)
  const [docCounts, setDocCounts] = useState({});
  // A faixa de alertas no topo recarrega sempre que a lista de pilotos ou os documentos mudam
  const [alertsKey, setAlertsKey] = useState(0);

  const toggleOpen = (id) => setOpenIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/di/pilots`, {
        withCredentials: true,
        params: showInactive ? { include_inactive: "true" } : {},
      });
      setPilots(r.data || []);
      setAlertsKey((k) => k + 1);
      // Se a contagem falhar, a lista continua, só sem o botão dos documentos
      axios.get(`${API}/di/documents/counts/pilot`, { withCredentials: true })
        .then((c) => setDocCounts(c.data || {}))
        .catch(() => setDocCounts({}));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar pilotos");
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (p) => {
    if (!window.confirm(`Eliminar piloto ${p.name}? Os alertas associados também serão apagados.`)) return;
    try {
      await axios.delete(`${API}/di/pilots/${p.id}`, { withCredentials: true });
      toast.success("Piloto eliminado");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a eliminar"); }
  };

  const toggleActive = async (p) => {
    const next = !(p.active !== false);
    try {
      await axios.patch(`${API}/di/pilots/${p.id}/active`, { active: next }, { withCredentials: true });
      toast.success(next ? "Piloto reactivado" : "Piloto desactivado");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a actualizar estado"); }
  };

  const counts = pilots.reduce((acc, p) => {
    const ok = p.operational_state?.operacional;
    if (ok) acc.ok += 1; else acc.bad += 1;
    return acc;
  }, { ok: 0, bad: 0 });

  // Clicar num alerta abre a ficha do piloto (se estiver na lista)
  const abrirPiloto = (id) => {
    const p = pilots.find((x) => x.id === id);
    if (p) { setEditing(p); setCreating(false); }
  };

  return (
    <div className="space-y-4" data-testid="di-pilots-panel">
      <AlertsBanner key={alertsKey} target="pilot" pilots={pilots} onJumpToPilot={abrirPiloto} />
      <StudentsWithoutPilotCard />
      <PendingFlightRequests />
      <PendingPilotDocuments onChanged={() => setAlertsKey((k) => k + 1)} />
      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-orange-300">
            <FileText className="w-5 h-5" /> Pilotos Regulares
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" data-testid="di-pilots-count-ok">● {counts.ok} operacionais</span>
              <span className="px-2 py-1 rounded-full bg-red-500/15 text-red-300 border border-red-500/40" data-testid="di-pilots-count-bad">● {counts.bad} não operacionais</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer" data-testid="di-pilots-show-inactive-label">
                <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="h-3 w-3 accent-orange-400" data-testid="di-pilots-show-inactive" />
                Mostrar inactivos
              </label>
              <Button size="sm" variant="outline" onClick={() => setImporting(true)} data-testid="di-pilots-import-btn"><Upload className="w-4 h-4 mr-1" /> Importar CSV</Button>
              <Button size="sm" className="btn-cockpit" onClick={() => { setEditing(null); setCreating(true); }} data-testid="di-pilots-add-btn"><Plus className="w-4 h-4 mr-1" /> Novo piloto</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="py-10 text-center text-slate-500 text-sm">A carregar...</div>
      ) : pilots.length === 0 ? (
        <div className="py-10 text-center text-slate-500 text-sm border border-dashed border-cockpit-border rounded">
          Nenhum piloto registado. Carrega em <strong>Novo piloto</strong> para começar.
        </div>
      ) : (
        <div className="space-y-3" data-testid="di-pilots-list">
          {pilots.map((p) => (
            <PilotCard
              key={p.id}
              pilot={p}
              expanded={openIds.has(p.id)}
              docCount={docCounts[p.id] || 0}
              onToggle={() => toggleOpen(p.id)}
              onEdit={() => { setEditing(p); setCreating(false); }}
              onDelete={() => remove(p)}
              onToggleActive={() => toggleActive(p)}
              onMerge={p.kind === "avulso" ? () => setMergeFor(p) : null}
              onPromoted={() => { refresh(); onInstructorCreated?.(); }}
            />
          ))}
        </div>
      )}

      {(creating || editing) && (
        <PilotForm
          pilot={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); refresh(); }}
        />
      )}

      {importing && (
        <PilotsImportDialog
          onClose={() => setImporting(false)}
          onImported={() => { setImporting(false); refresh(); }}
        />
      )}

      {mergeFor && (
        <PilotMergeDialog
          source={mergeFor}
          candidates={pilots.filter((p) => p.id !== mergeFor.id && p.active !== false)}
          onClose={() => setMergeFor(null)}
          onMerged={() => { setMergeFor(null); refresh(); }}
        />
      )}
    </div>
  );
}
